(() => {
  if (customElements.get('cc-product-videos')) return;

  class ProductVideos extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.track = this.querySelector('.cc-product-videos-track');
      this.cards = [...this.querySelectorAll('[data-cc-video-open]')];
      this.dialog = this.querySelector('dialog');
      this.player = this.dialog.querySelector('video');
      this.stage = this.querySelector('.cc-product-video-stage');
      this.error = this.querySelector('.cc-product-video-error');
      this.nav = this.querySelector('.cc-product-videos-nav');
      this.previous = this.querySelector('[data-video-step="-1"]');
      this.next = this.querySelector('[data-video-step="1"]');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.querySelector('[data-video-dialog-nav]').hidden = this.cards.length < 2;
      this.setupPlayerControls(options);

      // Two sets on either side leave room for momentum on short (e.g. three-video) strips.
      this.cards.forEach((card, index) => { card.dataset.videoIndex = index; });
      for (const side of ['before-outer', 'before', 'after', 'after-outer']) {
        const copies = this.cards.map(card => {
          const copy = card.cloneNode(true);
          copy.dataset.videoClone = side;
          copy.removeAttribute('id');
          copy.removeAttribute('data-shopify-editor-block');
          copy.querySelectorAll('[id]').forEach(element => element.removeAttribute('id'));
          return copy;
        });
        if (side.startsWith('before')) this.track.prepend(...copies);
        else this.track.append(...copies);
      }
      this.stripCards = [...this.track.querySelectorAll('[data-cc-video-open]')];
      this.stripCards.forEach(card => card.addEventListener('click', event => {
        if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || typeof this.dialog.showModal !== 'function') return;
        event.preventDefault();
        this.openVideo(card);
      }, options));
      this.querySelector('[data-video-close]').addEventListener('click', () => this.dialog.close(), options);
      this.dialog.addEventListener('close', () => this.stopVideo(), options);
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
      }, options);
      this.player.addEventListener('error', () => { if (this.dialog.open) this.error.hidden = false; }, options);
      this.querySelectorAll('[data-video-dialog-step]').forEach(button => button.addEventListener('click', () => {
        this.changeVideo(Number(button.dataset.videoDialogStep));
      }, options));
      this.stage.addEventListener('touchstart', event => {
        this.resetSwipe();
        this.suppressClick = false;
        if (!this.dialog.open || this.cards.length < 2 || event.touches.length !== 1) return;
        const point = event.touches[0];
        // Controls and seeking must not start a video-to-video swipe.
        if (event.target.closest?.('[data-video-controls]')) return;
        if (this.player.controls && point.clientY >= this.player.getBoundingClientRect().bottom - 64) return;
        this.swipe = { id: point.identifier, x: point.clientX, y: point.clientY, time: event.timeStamp, locked: false };
      }, { ...options, passive: true });
      this.stage.addEventListener('touchmove', event => {
        if (!this.swipe) return;
        if (event.touches.length !== 1) { this.resetSwipe(); return; }
        const point = [...event.touches].find(touch => touch.identifier === this.swipe.id);
        if (point) this.moveSwipe(point, event);
      }, { ...options, passive: false });
      this.stage.addEventListener('touchend', event => {
        if (!this.swipe) return;
        const point = [...event.changedTouches].find(touch => touch.identifier === this.swipe.id);
        if (!point) return;
        this.moveSwipe(point, event);
        if (!this.swipe) return;
        const dx = point.clientX - this.swipe.x;
        const quick = Math.abs(dx) >= 12 && event.timeStamp - this.swipe.time < 300;
        const change = this.swipe.locked && (Math.abs(dx) >= 24 || quick);
        this.suppressClick = this.swipe.locked;
        this.resetSwipe();
        if (change) this.changeVideo(dx < 0 ? 1 : -1);
      }, { ...options, passive: false });
      this.stage.addEventListener('touchcancel', () => this.resetSwipe(), options);
      this.stage.addEventListener('click', event => {
        if (!this.suppressClick) return;
        this.suppressClick = false;
        event.preventDefault();
        event.stopPropagation();
      }, { ...options, capture: true });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) { this.resetSwipe(); this.player.pause(); }
      }, options);
      [this.previous, this.next].forEach(button => button.addEventListener('click', () => {
        if (!this.looping) return;
        this.normalizeScroll();
        const index = Math.round(this.track.scrollLeft / this.step) + Number(button.dataset.videoStep);
        this.track.scrollTo({ left: index * this.step, behavior: this.motion.matches ? 'instant' : 'smooth' });
      }, options));
      const settle = () => {
        clearTimeout(this.scrollTimer);
        if (!this.pointerDown && !this.touching) this.normalizeScroll();
      };
      this.track.addEventListener('pointerdown', () => { this.pointerDown = true; }, options);
      const release = () => {
        if (!this.pointerDown) return;
        this.pointerDown = false;
        clearTimeout(this.scrollTimer);
        this.scrollTimer = setTimeout(settle, 160);
      };
      document.addEventListener('pointerup', release, options);
      document.addEventListener('pointercancel', release, options);
      this.track.addEventListener('touchstart', () => {
        clearTimeout(this.scrollTimer);
        // Recenter before the browser starts panning, never during a held swipe.
        // This also gives a rapid follow-up swipe fresh room before reaching a physical end.
        this.normalizeScroll();
        this.touching = true;
      }, { ...options, passive: true });
      const endTouch = event => {
        if (!this.touching || event.touches.length) return;
        this.touching = false;
        clearTimeout(this.scrollTimer);
        this.scrollTimer = setTimeout(settle, 160);
      };
      document.addEventListener('touchend', endTouch, options);
      document.addEventListener('touchcancel', endTouch, options);
      this.track.addEventListener('scrollend', settle, options);
      this.track.addEventListener('scroll', () => {
        this.syncAccessibleCards();
        clearTimeout(this.scrollTimer);
        this.scrollTimer = setTimeout(settle, 160);
      }, { ...options, passive: true });
      this.resize = new ResizeObserver(() => this.syncNavigation());
      this.resize.observe(this.track);
      this.syncNavigation();
    }

    setupPlayerControls(options) {
      this.controls = this.querySelector('[data-video-controls]');
      if (!this.controls) return; // Retain native controls if older markup is cached.
      this.playButton = this.querySelector('[data-video-play]');
      this.muteButton = this.querySelector('[data-video-mute]');
      this.progress = this.querySelector('[data-video-progress]');
      this.controls.hidden = false;
      this.player.controls = false;
      this.playButton.addEventListener('click', () => {
        if (this.player.paused || this.player.ended) this.playVideo();
        else this.player.pause();
        this.showControls();
      }, options);
      this.muteButton.addEventListener('click', () => {
        this.player.muted = !this.player.muted;
        this.syncPlayerControls();
      }, options);
      this.progress.addEventListener('input', () => {
        const duration = this.player.duration;
        if (!Number.isFinite(duration) || duration <= 0) return;
        this.player.currentTime = Math.max(0, Math.min(duration, Number(this.progress.value) / 1000 * duration));
        this.syncProgress();
        this.showControls();
      }, options);
      for (const event of ['play', 'pause', 'ended', 'volumechange']) {
        this.player.addEventListener(event, () => this.syncPlayerControls(), options);
      }
      for (const event of ['timeupdate', 'durationchange', 'loadedmetadata', 'emptied']) {
        this.player.addEventListener(event, () => this.syncProgress(), options);
      }
      this.stage.addEventListener('click', event => {
        if (!event.target.closest?.('[data-video-controls]')) this.showControls();
      }, options);
      this.stage.addEventListener('pointermove', event => {
        if (event.pointerType === 'mouse') this.showControls();
      }, options);
      this.controls.addEventListener('focusin', () => this.showControls(), options);
      this.controls.addEventListener('focusout', () => this.showControls(), options);
      this.syncPlayerControls();
      this.syncProgress();
    }

    showControls() {
      if (!this.controls) return;
      clearTimeout(this.controlsTimer);
      this.controls.classList.remove('is-hidden');
      if (!this.dialog.open || this.player.paused || this.player.ended) return;
      this.controlsTimer = setTimeout(() => {
        // Keyboard users keep their focused control; paused videos keep a visible Play button.
        if (this.dialog.open && !this.player.paused && !this.player.ended && !this.controls.querySelector(':focus-visible')) {
          this.controls.classList.add('is-hidden');
        }
      }, 1000);
    }

    syncPlayerControls() {
      if (!this.controls) return;
      const paused = this.player.paused || this.player.ended;
      this.controls.dataset.paused = String(paused);
      this.controls.dataset.muted = String(this.player.muted);
      this.playButton.setAttribute('aria-label', this.player.ended ? 'Replay video' : paused ? 'Play video' : 'Pause video');
      this.muteButton.setAttribute('aria-label', this.player.muted ? 'Unmute video' : 'Mute video');
      this.showControls();
    }

    syncProgress() {
      if (!this.progress) return;
      const duration = this.player.duration;
      const ready = Number.isFinite(duration) && duration > 0;
      const time = ready ? Math.max(0, Math.min(duration, this.player.currentTime || 0)) : 0;
      this.progress.disabled = !ready;
      this.progress.value = ready ? String(Math.round(time / duration * 1000)) : '0';
      this.progress.style.setProperty('--cc-video-progress', `${Number(this.progress.value) / 10}%`);
      const format = seconds => `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;
      this.progress.setAttribute('aria-valuetext', ready ? `${format(time)} of ${format(duration)}` : 'Loading video');
    }

    playVideo() {
      if (this.player.ended) this.player.currentTime = 0;
      this.player.play().catch(() => this.syncPlayerControls());
    }

    syncNavigation() {
      const width = this.track.clientWidth;
      if (!width || width === this.width) return;
      const count = this.cards.length;
      const oldIndex = this.step ? (this.track.scrollLeft - (this.looping ? 2 * this.cycle : 0)) / this.step : 0;
      const index = count ? ((Math.round(oldIndex) % count) + count) % count : 0;
      this.width = width;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      this.step = (this.cards[0]?.getBoundingClientRect().width || 0) + gap;
      this.cycle = this.step * count;
      // Keep the circular strip navigable even when every unique video fits.
      this.looping = count > 1;
      this.track.classList.toggle('is-looping', this.looping);
      this.nav.hidden = !this.looping;
      this.previous.disabled = this.next.disabled = !this.looping;
      this.track.scrollTo({ left: this.looping ? 2 * this.cycle + index * this.step : 0, behavior: 'instant' });
      this.syncAccessibleCards();
    }

    syncAccessibleCards() {
      const bounds = this.track.getBoundingClientRect();
      this.stripCards.forEach(card => {
        const rect = card.getBoundingClientRect();
        const visible = (!card.dataset.videoClone || this.looping) && Math.min(rect.right, bounds.right) - Math.max(rect.left, bounds.left) > 3;
        card.inert = !visible;
        card.setAttribute('aria-hidden', String(!visible));
      });
    }

    normalizeScroll() {
      if (!this.looping) return;
      const left = this.track.scrollLeft;
      if (left >= 2 * this.cycle - 1 && left < 3 * this.cycle - 1) return;
      let offset = ((left % this.cycle) + this.cycle) % this.cycle;
      // Scroll-snap rounds fractional pixels; a seam just below the cycle is still the first card.
      if (offset < 1 || this.cycle - offset < 1) offset = 0;
      this.track.scrollTo({ left: 2 * this.cycle + offset, behavior: 'instant' });
      this.syncAccessibleCards();
    }

    moveSwipe(point, event) {
      const dx = point.clientX - this.swipe.x, dy = point.clientY - this.swipe.y;
      if (!this.swipe.locked) {
        if (Math.abs(dy) >= 12 && Math.abs(dy) > Math.abs(dx) * 1.5) { this.resetSwipe(); return; }
        if (Math.abs(dx) < 6 || Math.abs(dx) < Math.abs(dy) * .75) return;
        this.swipe.locked = true;
        this.transitionAnimation?.cancel();
      }
      if (event.cancelable) event.preventDefault();
      if (!this.motion.matches) this.stage.style.transform = `translateX(${Math.max(-80, Math.min(80, dx * .4))}px)`;
    }

    resetSwipe() {
      this.swipe = null;
      this.stage.style.transform = '';
    }

    changeVideo(step) {
      if (!this.dialog.open || this.cards.length < 2) return;
      const index = (this.cards.indexOf(this.trigger) + step + this.cards.length) % this.cards.length;
      this.openVideo(this.cards[index], step);
    }

    openVideo(card, direction = 0) {
      const alreadyOpen = this.dialog.open;
      this.resetSwipe();
      this.transitionAnimation?.cancel();
      // A strip video must never play over audio from the main product gallery.
      this.closest('[data-cc-product]')?.querySelectorAll('.cc-pdp-stage video').forEach(video => video.pause());
      this.trigger = this.cards[Number(card.dataset.videoIndex)];
      this.player.pause();
      this.error.hidden = true;
      this.querySelector('[data-video-dialog-title]').textContent = card.dataset.videoTitle;
      this.player.setAttribute('aria-label', card.dataset.videoTitle);
      this.querySelector('[data-video-fallback]').href = card.href;
      this.player.poster = card.querySelector('img')?.src || '';
      this.player.src = card.href;
      if (this.progress) {
        this.progress.value = '0';
        this.progress.disabled = true;
        this.progress.style.setProperty('--cc-video-progress', '0%');
        this.progress.setAttribute('aria-valuetext', 'Loading video');
      }
      if (!alreadyOpen) {
        this.player.muted = false;
        this.dialog.showModal();
      }
      if (direction && !this.motion.matches) {
        this.transitionAnimation = this.stage.animate([
          { transform: `translateX(${direction * 24}px)`, opacity: .5 },
          { transform: 'translateX(0)', opacity: 1 }
        ], { duration: 180, easing: 'ease-out' });
      }
      this.playVideo();
      this.syncPlayerControls();
    }

    stopVideo() {
      this.resetSwipe();
      this.transitionAnimation?.cancel();
      this.player.pause();
      this.player.removeAttribute('src');
      this.player.load();
      clearTimeout(this.controlsTimer);
      const returnCard = this.stripCards.find(card => !card.inert && card.dataset.videoIndex === this.trigger?.dataset.videoIndex);
      if (returnCard?.isConnected) returnCard.focus({ preventScroll: true });
      else if (this.trigger?.isConnected) this.next.focus({ preventScroll: true });
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.resize?.disconnect();
      if (this.dialog?.open) this.dialog.close();
      if (this.player) this.stopVideo();
      clearTimeout(this.scrollTimer);
      this.stripCards?.filter(card => card.dataset.videoClone).forEach(card => card.remove());
      this.cards?.forEach(card => { card.inert = false; card.removeAttribute('aria-hidden'); });
      this.width = 0;
      this.pointerDown = false;
      this.touching = false;
      this.abort = null;
    }
  }
  customElements.define('cc-product-videos', ProductVideos);
})();
